import { container } from 'tsyringe'
import Vue from 'vue'
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators'
import store from '@/app/ui/store'
import { WhiteSellerPresenter } from '@/app/ui/presenters/WhiteSellerPresenter'
import {
  CreateWhiteSellerApiRequest,
  DeleteWhiteSellerApiRequest,
  ShowSellerPhoneRequest,
  UpdateWhiteSellerApiRequest,
} from '@/data/payload/api/WhiteSellerRequest'
import { Utils } from '@/app/infrastructures/misc'
import {
  WhiteListSeller,
  WhiteSeller,
  WhiteSellerCorporateClient,
  WhiteSellerPhone,
  WhiteSellerPickupPoint,
  WhiteSellerBoundPickupPoints,
} from '@/domain/entities/WhiteSeller'
import { Pagination } from '@/domain/entities/Pagination'
import MainController from './MainController'

export interface WhiteSellerPayload {
  sellerName: string
  // Not sent for Corporate (COMPANY) sellers
  sellerPhone?: string
  status: string
  agentId?: number[]
  sellerRole: string
  codHandling?: string
  courierId?: number
  assignType?: string
  // Corporate (COMPANY) only
  clientId?: number
  pcaIds?: number[]
}

function notifyLookupError(
  title: string,
  error: { status?: number; error?: { message?: { en?: string } } }
): void {
  Vue.notify({
    title,
    text:
      error?.status === 400 || error?.status === 422
        ? error?.error?.message?.en
        : 'Something wrong',
    type: 'error',
    duration: 5000,
  })
}

export interface WhiteSellerState {
  isLoading: boolean
  dataWhiteListSeller: WhiteSeller[]
  dataWhiteSeller: WhiteSeller
  paginationData: Pagination
  isAddWhiteSellerSuccess: boolean
  isEditWhiteSellerSuccess: boolean
  isDeleteWhiteSellerSuccess: boolean
}

@Module({
  namespaced: true,
  dynamic: true,
  store,
  name: 'marketplace',
})
class WhiteSellerController extends VuexModule implements WhiteSellerState {
  private presenter: WhiteSellerPresenter = container.resolve(
    WhiteSellerPresenter
  )
  public isLoading = false
  public loadingExport = false
  public dataWhiteListSeller = [new WhiteSeller()]
  public dataWhiteSeller = new WhiteSeller()
  public paginationData = new Pagination()
  public isAddWhiteSellerSuccess = false
  public isEditWhiteSellerSuccess = false
  public isDeleteWhiteSellerSuccess = false

  @Action({ rawError: true })
  public getAll(params: Record<string, any>) {
    this.setLoading(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getAll(formattedParams)
      .then(res => {
        if (res.data) this.setDataWhiteList(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch White List Seller Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public async getDetail(id: string) {
    this.setLoading(true)

    return this.presenter
      .get(id)
      .then(res => {
        if (res) this.setDataWhiteSeller(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Detail White Seller Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public async create(payload: WhiteSellerPayload): Promise<boolean> {
    this.setLoading(true)
    try {
      const res = await this.presenter.create(
        new CreateWhiteSellerApiRequest(
          payload.sellerName,
          payload.sellerPhone,
          payload.agentId,
          payload.sellerRole,
          payload.status,
          payload.codHandling,
          payload.courierId,
          payload.assignType,
          payload.clientId,
          payload.pcaIds
        )
      )
      this.setIsAddWhiteSellerSuccess(res)
      return res
    } catch (error) {
      Vue.notify({
        title: 'Create White Seller Failed',
        text:
          error.status === 400 || error.status === 422
            ? error.error.message.en
            : 'Something wrong',
        type: 'error',
        duration: 5000,
      })
      this.setIsAddWhiteSellerSuccess(false)
      return false
    } finally {
      this.setLoading(false)
    }
  }

  @Action({ rawError: true })
  public async getCorporateClients(
    name: string
  ): Promise<WhiteSellerCorporateClient[]> {
    try {
      return await this.presenter.getCorporateClients(
        Utils.toInstance(
          new Map(),
          JSON.stringify({ name: encodeURIComponent(name) }),
          'snake_case'
        )
      )
    } catch (error) {
      notifyLookupError('Fetch Corporate Client Failed', error)
      throw error
    }
  }

  @Action({ rawError: true })
  public async getCorporatePickupPoints(
    clientId: number
  ): Promise<WhiteSellerPickupPoint[]> {
    try {
      return await this.presenter.getCorporatePickupPoints(
        Utils.toInstance(
          new Map(),
          JSON.stringify({ clientId }),
          'snake_case'
        )
      )
    } catch (error) {
      notifyLookupError('Fetch Corporate Address Failed', error)
      throw error
    }
  }

  @Action({ rawError: true })
  public async getBoundPickupPoints(payload: {
    id: string
    page?: number
    limit?: number
    reveal?: boolean
  }): Promise<WhiteSellerBoundPickupPoints> {
    try {
      const { id, page = 1, limit = 50, reveal = false } = payload
      const params = Utils.toInstance(
        new Map(),
        JSON.stringify({ page, limit, reveal }),
        'snake_case'
      )
      return await this.presenter.getBoundPickupPoints(id, params)
    } catch (error) {
      notifyLookupError('Fetch Bound Pickup Points Failed', error)
      throw error
    }
  }

  @Action({ rawError: true })
  public async updateWhiteSeller(payload: {
    id: number
    name: string
    phone?: string
    status: string
    agentId?: number[]
    role: string
    codHandling?: string
    courierId?: number
    assignType?: string
    clientId?: number
    pcaIds?: number[]
  }): Promise<boolean> {
    this.setLoading(true)
    try {
      const res = await this.presenter.update(
        new UpdateWhiteSellerApiRequest(
          payload.id,
          payload.name,
          payload.phone,
          payload.status,
          payload.agentId,
          payload.role,
          payload.codHandling,
          payload.courierId,
          payload.assignType,
          payload.clientId,
          payload.pcaIds
        )
      )
      this.setIsEditWhiteSellerSuccess(res)
      return res
    } catch (error) {
      Vue.notify({
        title: 'Update White Seller Failed',
        text:
          error.status === 400 || error.status === 422
            ? error.error.message.en
            : 'Something wrong',
        type: 'error',
        duration: 5000,
      })
      this.setIsEditWhiteSellerSuccess(false)
      return false
    } finally {
      this.setLoading(false)
    }
  }

  @Action({ rawError: true })
  public deleteWhiteSeller(payload: { ids: number[] }) {
    this.setLoading(true)

    this.presenter
      .delete(new DeleteWhiteSellerApiRequest(payload.ids))
      .then(() => {
        this.setIsDeleteWhiteSellerSuccess(true)
      })
      .catch(error => {
        Vue.notify({
          title: 'Delete White Seller Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
        this.setIsDeleteWhiteSellerSuccess(false)
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public exportWhiteListSeller() {
    this.setLoadingExport(true)
    this.presenter
      .export()
      .then(res => {
        window.open(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Export White List Seller Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoadingExport(false)
      })
  }

  @Action({ rawError: true })
  public getExportWhiteList() {
    this.setLoadingExport(true);
    this.presenter
      .getExportWhiteList()
      .then(async (res) => {
        await MainController.prependReportList(res)
        MainController.setReportData(MainController.reportData);
      })
      .catch(error => {
        Vue.notify({
          title: 'Export White List Failed',
          text:
            error.status === 400 || error.status === 422
              ? error?.error?.message?.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      }).finally(() => {
        this.setLoadingExport(false);
      })
  }

  @Action({ rawError: true })
  public async showWhitelistSellerPhone(payload: ShowSellerPhoneRequest) {
    try {
      const resp = await this.presenter.showWhitelistSellerPhone(payload);
      return resp
    } catch (error) {
      Vue.notify({
        title: 'Show Whitelist Seller Phone Failed',
        text:
        error.status === 400 || error.status === 422
          ? error?.error?.message?.en
          : 'Something wrong',
        type: 'error',
        duration: 5000,
      })
      return new WhiteSellerPhone("");
    }
  }

  @Mutation
  public setLoading(bool: boolean) {
    this.isLoading = bool
  }

  @Mutation
  public setLoadingExport(bool: boolean) {
    this.loadingExport = bool
  }

  @Mutation
  public setDataWhiteList(list: WhiteListSeller) {
    this.dataWhiteListSeller = <WhiteSeller[]>list.data
    this.paginationData = <Pagination>list.pagination
  }

  @Mutation
  private setDataWhiteSeller(list: WhiteSeller) {
    this.dataWhiteSeller = list
  }

  @Mutation
  public setIsAddWhiteSellerSuccess(bool: boolean) {
    this.isAddWhiteSellerSuccess = bool
  }

  @Mutation
  public setIsEditWhiteSellerSuccess(bool: boolean) {
    this.isEditWhiteSellerSuccess = bool
  }

  @Mutation
  public setIsDeleteWhiteSellerSuccess(bool: boolean) {
    this.isDeleteWhiteSellerSuccess = bool
  }
}

export default getModule(WhiteSellerController)
